/** @jsxImportSource @emotion/react */
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { Button, Input } from "reactstrap";
import { CATEGORIES_WITH_CHECK_BUTTON, LAYER_TAB_CATEGORIES } from "../../../../actions/Layers/constants";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { headerLinks } from "../../../../actions/Tenants/config/constants";
import { layerFormFields } from "../../../../actions/Tenants/config/layerFormFields";
import { useTimezoneSelect } from "../../../../hooks";
import { isOfflineChecksAllowedForTenant } from "../../../../offline/allowedTenants";
import MetaForm from "../../../Forms/MetaForm";
import SingleSelect from "../../../Forms/SingleSelect";
import { GLOBAL_FILTERS_ON_TABS } from "../../Layers/LayerStart";
import { RipeningDenominator } from "../../Ripening/hooks/getRipeningClasses";
import FieldConfigForm from "./FieldConfigForm";
import FilterableConfigList from "./FilterableConfigList";
import FormConfigForm from "./FormConfigForm";
import NavigationConfigForm from "./NavigationConfigForm";
import OptionsConfigForm from "./OptionsConfigForm";
import RegisterKeyConfigList, { useRegisterKeyOptions } from "./RegisterKeyConfigList";
import RipeningRoomMetricCardsConfigForm from "./RipeningRoomMetricCardsConfigForm";
import { yesOrNoRadio } from "./util";

// * EFQ-3850 — small text input that previews the Day.js format the
// * tenant typed against the current time, so admins notice typos like
// * `dd-MM-YYYY` (Day.js `dd` = weekday, not day-of-month) immediately.
function DateFormatInput({
    name,
    placeholder,
    value,
    setValue,
}: {
    name: string;
    placeholder: string;
    value?: string | null;
    setValue: (v: string) => void;
}) {
    const effective = (typeof value === "string" && value.length > 0) ? value : placeholder;
    const preview = (() => {
        try {
            return dayjs().format(effective);
        } catch {
            return "(invalid format)";
        }
    })();
    return (
        <div>
            <Input
                type="text"
                name={name}
                placeholder={placeholder}
                value={value || ""}
                onChange={(e) => setValue(e.target.value)}
            />
            <small className="text-muted">Preview: <code>{preview}</code></small>
        </div>
    );
}

// SUPPLIER PORTAL CONFIG FILE
export const defaultRootConfig = {
    text: "root",
    value: "root",
    logo_path: null,
    home_tab: null,
    date_format: "DD-MM-YYYY",
    datetime_format: "HH:mm DD-MM-YYYY",
    primary_header_links: [
        { path: headerLinks.home, label: "Overview" }
    ],
    secondary_header_links: [
        { path: headerLinks.settings, label: "Settings" },
        { path: headerLinks.logout, label: "Logout" },
    ],
    overview_general_filter_form: []
};

// * Decision-to-ripe rules (pre-ripening) — pallet-attribute → auto proposal
// * used right after quality check and before ripening.
type RipeningRule = {
    fruit_type?: string;
    conditions?: Record<string, string[]>;
    [key: string]: any;
    min_ripening_value?: number | null;
    proposal?: "skip" | "ripen" | "manual" | "standby";
};

const RIPENING_RULE_PROPOSALS: { value: RipeningRule["proposal"]; label: string }[] = [
    { value: "skip", label: "Skip ripening" },
    { value: "ripen", label: "Send to ripening" },
    { value: "manual", label: "Manual (operator decides)" },
    { value: "standby", label: "Stand-By (defer, no SAP send)" },
];

const RIPENING_RULE_CONDITION_KEYS: { key: string; label: string; description: string }[] = [
    { key: "article_name", label: "Article numbers", description: "Comma-separated SAP article numbers (stored on the pallet's `article_name` attribute)." },
    { key: "box_size", label: "Box sizes", description: "Comma-separated box sizes." },
    { key: "fruit_variety", label: "Varieties", description: "Comma-separated fruit varieties." },
    { key: "country", label: "Origins (country)", description: "Comma-separated country values." },
    { key: "end_client", label: "Customers (end client)", description: "Comma-separated end clients." },
];

const default_ripening_decision_rule = {
    field: {
        fruit_type: "",
        conditions: {},
        proposal: "manual",
    },
    display_filter: {},
};

const splitCommaValues = (value: string): string[] => String(value || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

const joinCommaValues = (values?: string[] | string): string => {
    if (Array.isArray(values)) return values.join(", ");
    return values || "";
};

function RipeningDecisionRulesEditor({
    name,
    title,
    description,
    fields,
    setArray,
}: {
    name: string;
    title: string;
    description: string;
    fields: RipeningRule[];
    setArray: (rules: RipeningRule[]) => void;
}) {
    const getFieldTitle = (rule: RipeningRule) => {
        const proposal = (rule?.proposal || "manual").toUpperCase();
        const fruit = (rule?.fruit_type || "").trim() || "any fruit";
        return <span><b>{proposal}</b> <span className="text-muted">| fruit: {fruit}</span></span>;
    };

    const getFieldForm = (rule: RipeningRule) => {
        const conditions = rule?.conditions || {};
        return [
            {
                label: "Decision to ripe outcome",
                name: "proposal",
                type: "radio",
                options: RIPENING_RULE_PROPOSALS.map((o) => ({ value: o.value, label: o.label })),
                description: "Outcome used right after quality check and before ripening.",
            },
            {
                label: "Fruit type",
                name: "fruit_type",
                type: "text",
                placeholder: "e.g. avocado, banana (empty = any fruit)",
                description: "Leave empty to apply to all fruits.",
            },
            {
                label: "Minimum ripening value",
                name: "min_ripening_value",
                type: "number",
                placeholder: "e.g. 2.5",
                description: "Optional. Rule only matches when measured value is >= this threshold.",
            },
            { type: "header", label: "Conditions (all must match when filled)" },
            ...RIPENING_RULE_CONDITION_KEYS.map((item) => ({
                label: item.label,
                name: `${item.key}_csv`,
                type: "text",
                default_value: joinCommaValues(rule?.[`${item.key}_csv`] || conditions[item.key]),
                description: item.description,
            })),
        ];
    };

    return (
        <FilterableConfigList
            useDisplayFilter={false}
            name={name}
            title={title}
            description={description}
            fields={fields || []}
            setArray={(rules) => setArray((rules as RipeningRule[]).map((rule) => {
                const conditions = { ...(rule.conditions || {}) };
                const cleaned = { ...rule };
                RIPENING_RULE_CONDITION_KEYS.forEach(({ key }) => {
                    const csv = rule[`${key}_csv`];
                    if (typeof csv === "string") {
                        const values = splitCommaValues(csv);
                        if (values.length) conditions[key] = values;
                        else delete conditions[key];
                    }
                    // strip the UI-only CSV helper key so it is never persisted
                    delete cleaned[`${key}_csv`];
                });
                return { ...cleaned, conditions };
            })) as any}
            defaultField={default_ripening_decision_rule}
            getFieldTitle={getFieldTitle as any}
            getFieldForm={getFieldForm as any}
        />
    );
}

const default_ripening_room_start_cycle_form = [
    {
        field: {
            label: "Ripening target",
            name: "ripening_target",
            type: "single-select",
            mandatory: true,
            options: [],
            inherit_ripening_classes_from_fruit_config: "pressure_avg"
        },
        display_filter: {
            fruit_types: ["avocado"],
            exclude_fruit_types: [],
            layer_types: []
        }
    },
    {
        field: {
            label: "Ripening target",
            name: "ripening_target",
            type: "single-select",
            mandatory: true,
            options: [],
            inherit_ripening_classes_from_fruit_config: "maturity_avg"
        },
        display_filter: {
            fruit_types: ["mango"],
            exclude_fruit_types: [],
            layer_types: []
        }
    },
    {
        field: {
            label: "Ripening target",
            name: "ripening_target",
            type: "single-select",
            mandatory: true,
            options: [],
            inherit_ripening_classes_from_fruit_config: "external_color_avg"
        },
        display_filter: {
            fruit_types: ["banana"],
            exclude_fruit_types: [],
            layer_types: []
        }
    },
    {
        field: {
            label: "Ripening program",
            name: "ripening_program",
            type: "single-select",
            mandatory: false,
            options: [
                {
                    label: "1 times gas",
                    value: "1-times-gas"
                },
                {
                    label: "2 times gas",
                    value: "2-times-gas"
                },
                {
                    label: "3 times gas",
                    value: "3-times-gas"
                }
            ]
        },
        display_filter: {
            fruit_types: [],
            exclude_fruit_types: [],
            layer_types: []
        }
    },
    {
        field: {
            label: "Start Date Program",
            name: "ripening_start_date",
            type: "date-radiobutton",
            mandatory: true,
            backward: "1",
            forward: "2"
        },
        display_filter: {
            fruit_types: [],
            exclude_fruit_types: [],
            layer_types: []
        }
    },
    {
        field: {
            label: "End Date Program",
            name: "ripening_end_date",
            type: "date-radiobutton",
            mandatory: true,
            backward: "0",
            forward: "8"
        },
        display_filter: {
            fruit_types: [],
            exclude_fruit_types: [],
            layer_types: []
        }
    },
    {
        field: {
            label: "Season",
            name: "season",
            type: "single-select",
            mandatory: true,
            options: [
                { value: "early", label: "Early" },
                { value: "middle", label: "Middle" },
                { value: "late", label: "Late" },
            ],
        },
        display_filter: {
            fruit_types: [],
            exclude_fruit_types: [],
            layer_types: []
        },
    },
    {
        field: {
            label: "Notes",
            name: "room_notes",
            type: "textarea"
        },
        display_filter: {
            fruit_types: [],
            exclude_fruit_types: [],
            layer_types: []
        },
        error: false
    }
];

const default_ripening_room_cycle_sidebar_fields: any[] = [
    {
        field: {
            field_id: "CUSTOM_RIPENING_ROOM_META",
            fieldname_getter: "ripening_program",
            label: "Program",
            value_if_null: "-",
            error: false,
            fieldname_getter_common_field: "ripening_program"
        },
        display_filter: {
            fruit_types: [],
            exclude_fruit_types: [],
            layer_types: []
        },
        error: false
    },
    {
        field: {
            field_id: "CUSTOM_RIPENING_ROOM_META",
            fieldname_getter: "room_notes",
            label: "Notes",
            value_if_null: "-",
            error: false,
            fieldname_getter_common_field: "room_notes"
        },
        display_filter: {
            fruit_types: [],
            exclude_fruit_types: [],
            layer_types: []
        },
        error: false
    }
];
const default_ripening_room_cycle_history_fields: any[] = [
    {
        field: {
            field_id: "CUSTOM_RIPENING_ROOM_META",
            fieldname_getter: "ripening_program",
            label: "Program",
            value_if_null: "-",
            error: false,
            fieldname_getter_common_field: "ripening_program"
        },
        display_filter: {
            fruit_types: [],
            exclude_fruit_types: [],
            layer_types: []
        },
        error: false
    }
];

export default function RootConfigForm({ config_data, setConfigData }) {
    const config = useConfig();
    const { tenant_id } = useConfig();
    const { options } = useTimezoneSelect();
    const { registerKeyOptions, registerLabelByKey } = useRegisterKeyOptions();
    // Offline-checks config is hard-coded to Bakker (plus the playwright e2e
    // tenant) — see frontend/src/offline/allowedTenants.ts. Hides the toggle
    // in the admin GUI for every other tenant so it can't be flipped on
    // accidentally. The runtime gate in `isOfflineChecksEnabled` rejects
    // non-allowlist tenants independently.
    const showOfflineChecksConfig = isOfflineChecksAllowedForTenant(tenant_id);

    const form = [
        {
            label: "Value",
            description: "What is the slug or identifier of this layer type. Used in the database and in urls.",
            name: "value",
            type: "single-select",
            readOnly: true,
            default: "root",
            options: [{ value: "root", label: "root" }]
        },
        {
            type: "element",
            name: "timezone",
            label: "Tenant timezone",
            description: "IANA timezone name for this tenant. Used to determine midnight for daily business rules. If not set daily business rules don't run.",
            el: <SingleSelect
                field="timezone"
                label="Timezone"
                options={options}
                post_value={config_data.timezone}
                setValue={(_, v) => setConfigData({ ...config_data, timezone: v })}
            />,
        },
        {
            type: "header",
            label: "Navigation",
        },
        {
            type: "single-select",
            name: "home_tab",
            label: "Default home tab",
            description: "Select which tab to navigate to when clicking the logo",
            options: config.stages.map((stage) => ({ value: stage.value, label: stage.text })),
        },
        {
            type: "element",
            label: "Primary header links",
            description: "Primary navigation links in the header",
            el: <NavigationConfigForm
                name="primary_header_links"
                title="Primary header links"
                description="Primary navigation links in the header"
                fields={config_data.primary_header_links}
                setArray={(value) => setConfigData({ ...config_data, primary_header_links: value })}
            />
        },
        {
            type: "element",
            label: "Secondary header links",
            description: "Secondary navigation links in the header",
            el: <NavigationConfigForm
                name="secondary_header_links"
                title="Secondary header links"
                description="Secondary navigation links in the header"
                fields={config_data.secondary_header_links}
                setArray={(value) => setConfigData({ ...config_data, secondary_header_links: value })}
            />
        },
        {
            type: "header",
            label: "Branding",
        },
        {
            label: "Client Logo",
            name: "logo_path",
            type: "file-select",
            accept: ["image/webp", "image/png", "image/jpeg"],
            upload_config_image: true,
            description: "Upload your client logo (WebP, PNG, or JPEG format). This logo will be used in reports and other tenant-specific displays.",
        },
        {
            type: "header",
            label: "Date formats",
        },
        {
            type: "element",
            name: "date_format_preview",
            label: "Default date format",
            description: "Default format for date-only fields. Per-field overrides still win. Uses Day.js tokens — see https://day.js.org/docs/en/display/format. Examples: DD-MM-YYYY, MM/DD/YYYY, YYYY-MM-DD.",
            el: <DateFormatInput
                name="date_format"
                placeholder="DD-MM-YYYY"
                value={config_data.date_format}
                setValue={(v) => setConfigData({ ...config_data, date_format: v || undefined })}
            />,
        },
        {
            type: "element",
            name: "datetime_format_preview",
            label: "Default datetime format",
            description: "Default format for date+time fields. Per-field overrides still win. Uses Day.js tokens — see https://day.js.org/docs/en/display/format. Examples: HH:mm DD-MM-YYYY, YYYY-MM-DD HH:mm, MM/DD/YYYY hh:mm A.",
            el: <DateFormatInput
                name="datetime_format"
                placeholder="HH:mm DD-MM-YYYY"
                value={config_data.datetime_format}
                setValue={(v) => setConfigData({ ...config_data, datetime_format: v || undefined })}
            />,
        },
        {
            type: "header",
            label: "Filters",
        },
        {
            label: "Default fruit type for layer creation",
            name: "default_fruit_type",
            type: "single-select",
            options: config.fruit_types.map((i) => ({ value: i.value, label: i.text }))
        },
        {
            label: "Barcode scanner meta",
            name: "barcode_scanner_meta_fallback",
            type: "single-select",
            multi: true,
            return_array: true,
            description: "If barcode scanned can't match on label, search through this layer meta data for a match. For example SSCC code. For best experience only fill in meta keys that have a unique value for each pallet.",
            options: layerFormFields.map((i) => ({ value: i.name, label: `(layer) ${i.label}` }))
        },

        // Offline-checks fields are only rendered for allowlisted tenants
        // (Bakker + playwright-test). See allowedTenants.ts.
        ...(showOfflineChecksConfig ? [
            {
                type: "header",
                label: "Offline checks",
            },
            {
                label: "Enable offline checks",
                name: "enable_offline_checks",
                description: [
                    "Master kill-switch for the offline quality-check feature on this tenant.",
                    "Defaults to off — the app behaves exactly as it did before.",
                    "When set to Yes, the offline IndexedDB stores open, the service worker registers",
                    "(in production builds), the ETag interceptor activates on the four polling endpoints,",
                    "and the offline scan / fill / submit / sync flow becomes available.",
                    "Two more pieces of configuration are required for the feature to be useful:",
                    "at least one overview tab marked Offline cache: Yes (under Tabs),",
                    "and at least one check type marked Offline-enabled: Yes (under Check locations).",
                    "Without those, scanning offline finds no pallets and the offline check-type selector is empty.",
                ].join(" "),
                ...yesOrNoRadio,
            },
        ] : []),

        {
            type: "header",
            label: "Tab config"
        },
        {
            label: "Tab categories with Start Check button",
            name: "tab_categories_with_check_button",
            description: <div>What tab categories should show the big green button to start check. Will default to {CATEGORIES_WITH_CHECK_BUTTON.join(", ")}</div>,
            type: "single-select",
            multi: true,
            return_array: true,
            options: Object.keys(LAYER_TAB_CATEGORIES).map((i) => ({ value: LAYER_TAB_CATEGORIES[i], label: i }))
        },
        {
            label: "Global filters",
            description: "Global filters to enable on all tabs.",
            name: "global_filters_on_tabs",
            type: "single-select",
            multi: true,
            return_array: true,
            options: Object.values(GLOBAL_FILTERS_ON_TABS).map((i) => ({ value: i, label: i }))
        },
        {
            type: "header",
            label: "Decision to ripe (pre-ripening)"
        },
        {
            type: "element",
            name: "ripening_decision_rules",
            label: "Decision to ripe rules",
            description: "Configurable skip/ripen/manual/stand-by decision right after quality check and before ripening. Rules are evaluated in order — first match wins, so put specific rules before catch-alls.",
            el: <RipeningDecisionRulesEditor
                name="ripening_decision_rules"
                title="Decision to ripe rules"
                description="Rules are evaluated in order; first match wins."
                fields={config_data.ripening_decision_rules || []}
                setArray={(ripening_decision_rules) => setConfigData({ ...config_data, ripening_decision_rules })} />
        },
        {
            type: "header",
            label: "Ripening rooms list"
        },
        {
            type: "element",
            label: "Ripening room overview",
            description: "What fields and options to show in the ripening room overview screen.",
            el: <FieldConfigForm
                fields={config_data.ripening_room_overview_fields || []}
                description={<span>What fields to display on the ripening room overview screen (<Link to={`/tenants/${tenant_id}/facility-locations`}>list of rooms</Link>).</span>}
                useDisplayFilter={["fruit_types", "exclude_fruit_types"]}
                name="ripening_room_overview_fields"
                title="Ripening room overview"
                setArray={(ripening_room_overview_fields) => setConfigData({ ...config_data, ripening_room_overview_fields })} />
        },
        {
            type: "radio",
            name: "ripening_room_overview_fields_default_view",
            label: "Default view",
            description: "How to display the ripening room overview fields.",
            options: [
                { value: "list", label: "List" },
                { value: "grid", label: "Grid" }
            ]
        },
        {
            type: "number",
            name: "ripening_room_number_of_rooms_per_row",
            label: "Number of rooms per row in grid view",
            description: "How many rooms to show per row in the grid view of the ripening rooms.",
            placeholder: "9",
        },
        {
            type: "element",
            name: "ripening_group_by_fields",
            label: "Ripening room group by fields",
            description: "What fields to group by in the ripening room overview screen.",
            el: <OptionsConfigForm
                fields={config_data.ripening_group_by_fields || []}
                description="Options to group by in the ripening room screens."
                name="ripening_group_by_fields"
                useDisplayFilter={["fruit_types", "exclude_fruit_types"] as any}
                title="Ripening room group by fields"
                setArray={(ripening_group_by_fields) => setConfigData({ ...config_data, ripening_group_by_fields })} />
        },

        {
            type: "header",
            label: "Ripening room detail page"
        },
        {
            label: "Enable Signals feature",
            name: "ripening_room_enable_signals",
            description: "Master switch for the whole Signals feature: the top-nav Signals link, the Signals page, the rooms traffic-light, the room-detail signals banner/tab, and the admin Signal Rules page. Defaults to off.",
            ...yesOrNoRadio,
        },
        {
            type: "number",
            name: "ripening_room_days_before",
            label: "Days before",
            description: "How many days before to show in the ripening room.",
            placeholder: "2",
        },
        {
            type: "number",
            name: "ripening_room_days_after",
            label: "Days after",
            description: "How many days after to show in the ripening room.",
            placeholder: "2",
        },
        {
            type: "element",
            name: "ripening_room_sensor_graph_register_keys",
            label: "Sensor graph registers",
            description: "Registers to show in the ripening room sensor graph (ordered). Optionally override each line color.",
            el: (
                <RegisterKeyConfigList
                    name="ripening_room_sensor_graph_register_keys"
                    title="Sensor graph registers"
                    description="Registers to show in the ripening room sensor graph (ordered)."
                    fields={config_data.ripening_room_sensor_graph_register_keys || []}
                    setArray={(ripening_room_sensor_graph_register_keys) => setConfigData({ ...config_data, ripening_room_sensor_graph_register_keys })}
                    labelPlaceholder="Co2"
                    colorPlaceholder="#1f77b4"
                />
            )
        },
        {
            type: "element",
            name: "ripening_room_day_view_register_keys",
            label: "Day view registers",
            description: "Registers to show in ripening room day blocks and room conditions modal.",
            el: (
                <FilterableConfigList
                    name="ripening_room_day_view_register_keys"
                    title="Day view registers"
                    description="Registers to show in ripening room day blocks and room conditions modal (ordered)."
                    useDisplayFilter={["fruit_types", "exclude_fruit_types"]}
                    fields={(config_data.ripening_room_day_view_register_keys || []).map((item) => (
                        typeof item === "string" ? ({ key: item }) : item
                    ))}
                    setArray={(ripening_room_day_view_register_keys: any[]) => setConfigData({ ...config_data, ripening_room_day_view_register_keys })}
                    defaultField={{ key: "" }}
                    getFieldTitle={(field: any) => (
                        <span>
                            <b>{field?.label || registerLabelByKey?.[field?.key] || field?.key || "Select register"}</b>
                            {field?.key ? <span className="text-muted"> ({field.key})</span> : null}
                        </span>
                    )}
                    getFieldForm={(_object: any, setObject: any) => ([
                        {
                            label: "Register",
                            name: "key",
                            type: "single-select",
                            options: registerKeyOptions,
                            multi: false,
                            onchange_callback: ({ value }) => {
                                const nextLabel = registerLabelByKey?.[value] || value;
                                setObject!({ key: value, label: nextLabel });
                            }
                        },
                        {
                            label: "Label (optional)",
                            name: "label",
                            type: "text",
                            placeholder: "CO₂",
                            description: "Human-readable label (defaults to selected register label).",
                        },
                    ])}
                />
            ),
        },
        {
            label: "Enable fruit sensor graph",
            name: "ripening_room_enable_fruit_sensor_graph",
            description: "Enable the fruit sensor graph tab in the ripening room detail page. Shows RipeTap device measurements (firmness readings) over time.",
            ...yesOrNoRadio,
        },
        {
            name: "ripening_room_fruit_sensor_graph_register_keys",
            label: "Fruit sensor graph color registers",
            description: "Color sensor registers to show alongside RipeTap firmness data in the fruit sensor graph (ordered). Optionally override each axis/line color. The y-axis range defaults to the register configuration in the device management screen; set min/max here to override it for this graph only.",
            type: "element",
            el: (
                <RegisterKeyConfigList
                    name="ripening_room_fruit_sensor_graph_register_keys"
                    title="Fruit sensor graph color registers"
                    description="Color sensor registers to show alongside RipeTap firmness data in the fruit sensor graph (ordered)."
                    fields={config_data.ripening_room_fruit_sensor_graph_register_keys || []}
                    setArray={(ripening_room_fruit_sensor_graph_register_keys) => setConfigData({ ...config_data, ripening_room_fruit_sensor_graph_register_keys })}
                    labelPlaceholder="Eth"
                    colorPlaceholder="#ff7f0e"
                />
            )
        },
        {
            type: "element",
            label: "Ripening room overview",
            description: "What fields to display in the list of allocated pallets.",
            el: <FieldConfigForm
                fields={config_data.ripening_room_pallet_fields || []}
                description="What fields to display in the list of allocated pallets."
                useDisplayFilter={["fruit_types", "exclude_fruit_types"]}
                name="ripening_room_pallet_fields"
                title="Ripening room overview"
                setArray={(ripening_room_pallet_fields) => setConfigData({ ...config_data, ripening_room_pallet_fields })} />
        },
        {
            type: "element",
            label: "Ripening room metrics",
            description: "Show metrics in the sidebar of the ripening room screen.",
            el: <RipeningRoomMetricCardsConfigForm
                fields={config_data.ripening_room_metric_cards || []}
                description="Show metrics in the sidebar of the ripening room screen."
                name="ripening_room_metric_cards"
                title="Ripening room metrics"
                setArray={(ripening_room_metric_cards) => setConfigData({ ...config_data, ripening_room_metric_cards })} />

        },
        {
            type: "element",
            label: "Ripening room overview",
            description: "What fields to display on the ripening room side bar (first group).",
            el: <FieldConfigForm
                fields={config_data.ripening_room_sidebar_meta_fields_1 || []}
                description={<span>What fields to display on the ripening room side bar (first group)</span>}
                useDisplayFilter={["fruit_types", "exclude_fruit_types"]}
                name="ripening_room_sidebar_meta_fields_1"
                title="Ripening room overview"
                setArray={(ripening_room_sidebar_meta_fields_1) => setConfigData({ ...config_data, ripening_room_sidebar_meta_fields_1 })} />
        },
        {
            type: "element",
            label: "Ripening room overview",
            description: "What fields to display on the ripening room side bar (second group).",
            el: <FieldConfigForm
                fields={config_data.ripening_room_sidebar_meta_fields_2 || []}
                description={<span>What fields to display on the ripening room side bar (second group)</span>}
                useDisplayFilter={["fruit_types", "exclude_fruit_types"]}
                name="ripening_room_sidebar_meta_fields_2"
                title="Ripening room overview"
                setArray={(ripening_room_sidebar_meta_fields_2) => setConfigData({ ...config_data, ripening_room_sidebar_meta_fields_2 })} />
        },
        {
            type: "element",
            name: "ripening_room_edit_cycle_form",
            label: "Edit cycle form",
            deprecated: false,
            description: "A form to edit the active ripening cycle and update room meta data. Can be used for both editing and ending the cycle.",
            el: <FormConfigForm
                fields={config_data.ripening_room_edit_cycle_form || []}
                description=<span>A form to edit the active ripening cycle and update room meta data. Is used for both editing and ending the cycle.<Button color="link" onClick={() => setConfigData({ ...config_data, ripening_room_edit_cycle_form: default_ripening_room_start_cycle_form })}>Reset to default</Button></span>
                name="ripening_room_edit_cycle_form"
                title="Edit cycle form"
                setArray={(ripening_room_edit_cycle_form) => setConfigData({ ...config_data, ripening_room_edit_cycle_form })} />
        },
        {
            type: "element",
            name: "ripening_room_start_cycle_form",
            label: "Start Cycle Form",
            el: <FormConfigForm
                fields={config_data.ripening_room_start_cycle_form || []}
                description={<span>A form to collect information when starting a ripening cycle.<Button color="link" onClick={() => setConfigData({ ...config_data, ripening_room_start_cycle_form: default_ripening_room_start_cycle_form })}>Reset to default</Button></span>}
                name="ripening_room_start_cycle_form"
                title="Start Cycle Form"
                setArray={(ripening_room_start_cycle_form) => setConfigData({ ...config_data, ripening_room_start_cycle_form })} />
        },
        {
            type: "element",
            label: "Ripening cycle sidebar",
            description: "Extra fields shown in the ripening room sidebar for the selected cycle (can include more detail than the history table, e.g. notes).",
            el: <FieldConfigForm
                fields={config_data.ripening_room_cycle_sidebar_fields || []}
                description={<span>Fields for the selected-cycle block in the sidebar. Independent from the cycle history modal columns.<Button color="link" onClick={() => setConfigData({ ...config_data, ripening_room_cycle_sidebar_fields: default_ripening_room_cycle_sidebar_fields })}>Reset to default</Button></span>}
                useDisplayFilter={["fruit_types", "exclude_fruit_types"]}
                name="ripening_room_cycle_sidebar_fields"
                title="Ripening cycle sidebar (selected cycle)"
                setArray={(ripening_room_cycle_sidebar_fields) => setConfigData({ ...config_data, ripening_room_cycle_sidebar_fields })} />
        },
        {
            type: "element",
            label: "Ripening Cycle History table columns",
            description: "Columns in the ripening cycle history modal table (often a compact subset of the sidebar).",
            el: <FieldConfigForm
                fields={config_data.ripening_room_cycle_history_fields || []}
                description={<span>Columns for the cycle history modal table only.<Button color="link" onClick={() => setConfigData({ ...config_data, ripening_room_cycle_history_fields: default_ripening_room_cycle_history_fields })}>Reset to default</Button></span>}
                useDisplayFilter={["fruit_types", "exclude_fruit_types"]}
                name="ripening_room_cycle_history_fields"
                title="Ripening Cycle History table columns"
                setArray={(ripening_room_cycle_history_fields) => setConfigData({ ...config_data, ripening_room_cycle_history_fields })} />
        },
        {
            type: "single-select",
            name: "ripening_room_denominator_for_relative_classes",
            label: "Denominator for relative number of classes",
            description: "What denominator to use for the relative number of classes in the ripening room.",
            options: Object.values(RipeningDenominator).map((i) => ({ value: i, label: i }))
        },
        {
            type: "header",
            label: "Ripening room layout"
        },
        {
            label: "Disable allocation UI",
            name: "ripening_room_disable_allocation_ui",
            description: "Disable allocation UI in the ripening room. Set this to true if the client relies on the ERP/WHS to allocate pallets to the ripening room.",
            ...yesOrNoRadio
        },
        {
            name: "ripening_room_slot_allocation_from_erp",
            label: "Slot allocation in room",
            description: "Enable slot allocation in the ripening room. This will allow an ERP/WHS to allocate pallets to specific slots in the ripening room.",
            ...yesOrNoRadio
        },
        {
            label: "Enable Ripening Prediction",
            name: "enable_ripening_prediction",
            description: "Enable ripening prediction in the ripening room and in layer graphs. This requires the business rules for samrt sampling to be active.",
            ...yesOrNoRadio
        },
        {
            type: "text",
            name: "ripening_room_levels",
            label: "Ripening room levels",
            description: "Give a comma seperated list of the cell levels. For example: A,B,C or -1,0,1 or 1,2,3,4,5 or Ground Floor, First Floor, Second Floor. Defaults to Level 0, Level 1, Level 2",
            placeholder: "0,1,2",
        },
        {
            type: "text",
            name: "ripening_room_columns",
            label: "Ripening room columns",
            description: "Give a comma seperated list of the cell columns.  Use `hall` to define a hallway. For example: A,hall,B or 1,2,hall,3,5",
        },
        {
            type: "text",
            name: "ripening_room_rows",
            label: "Ripening room rows",
            description: "Give a comma seperated list of the cell rows. Use `hall` to define a hallway. For example: A,hall,B or 1,2,hall,3,5",
        },
        {
            type: "header",
            label: "Supplier portal config",
        },
        {
            type: "element",
            title: "Supplier portal table fields config",
            description: "What fields to display on the supplier's reports table.",
            el: <FieldConfigForm
                fields={config_data.supplier_portal_overview_fields || []}
                description="What fields to display on the supplier's reports table."
                name="supplier_portal_overview_fields"
                title="Supplier portal table fields config"
                setArray={(supplier_portal_overview_fields) => setConfigData({ ...config_data, supplier_portal_overview_fields })} />
        },
        {
            type: "header",
            label: "Client portal config",
        },
        {
            type: "element",
            title: "Client portal table fields config",
            description: "What fields to display on the client's reports table.",
            el: <FieldConfigForm
                fields={config_data.client_portal_overview_fields || []}
                description="What fields to display on the client's reports table."
                name="client_portal_overview_fields"
                title="Client portal table fields config"
                setArray={(client_portal_overview_fields) => setConfigData({ ...config_data, client_portal_overview_fields })} />
        },
        {
            label: "Packing list config",
            type: "header",
        },
        {
            type: "single-select",
            name: "default_parent_label_field",
            label: "Default parent label field",
            description: "What field to use as the parent label field in the packing list.",
            options: ["container_no", "purchase_order_number", "receipt", "order_number", "vessel_number", "voyage_number"].map((i) => ({ value: i, label: i }))
        },
        {
            type: "single-select",
            name: "default_child_label_field",
            label: "Default child label field",
            description: "What field to use as the child label field in the packing list.",
            options: ["supplier_pallet_number", "license_plate"].map((i) => ({ value: i, label: i }))
        },
        {
            type: "element",
            title: "Packing list table fields config",
            description: "What fields to display on the packing list's table.",
            el: <FieldConfigForm
                fields={config_data.packing_list_table_fields || []}
                description="What fields to display on the packing list's table."
                name="packing_list_table_fields"
                title="Packing list table fields config"
                setArray={(packing_list_table_fields) => setConfigData({ ...config_data, packing_list_table_fields })}
                useDisplayFilter={false} />
        },
        {
            type: "header",
            label: "Sourcing list config",
        },
        {
            type: "element",
            title: "Sourcing list table fields config",
            description: "What fields to display on the sourcing list's table.",
            el: <FieldConfigForm
                fields={config_data.sourcing_list_table_fields || []}
                description="What fields to display on the sourcing list's table."
                name="sourcing_list_table_fields"
                title="Sourcing list table fields config"
                setArray={(sourcing_list_table_fields) => setConfigData({ ...config_data, sourcing_list_table_fields })}
                useDisplayFilter={false} />
        },
        {
            type: "header",
            label: "Items config",
        },
        {
            type: "element",
            name: "tenant_items_meta_form",
            label: "Items meta form",
            description: "A form to manually set the meta data of the items.",
            el: <FormConfigForm
                fields={config_data.tenant_items_meta_form || []}
                description="A form to manually set the meta data of the items."
                name="tenant_items_meta_form"
                title="Items meta form"
                setArray={(tenant_items_meta_form) => setConfigData({ ...config_data, tenant_items_meta_form })} />
        },
        {
            type: "element",
            label: "Items table fields config",
            description: "What fields to display on the items form.",
            el: <FieldConfigForm
                fields={config_data.items_table_fields || []}
                description="What fields to display on the items table."
                name="items_table_fields"
                title="Items table fields config"
                setArray={(items_table_fields) => setConfigData({ ...config_data, items_table_fields })}
                useDisplayFilter={false} />
        },
        {
            type: "header",
            label: "Integrations",
        },
        {
            ...yesOrNoRadio,
            name: "send_ripening_cycle_closure_enabled",
            label: "Send ripening cycle closure to external service",
            description: "When enabled, a per-pallet closure message is sent to the tenant's external service (e.g. Bakker SAP) for every pallet related to a check when a ripening cycle ends.",
            default_value: false,
        },
        {
            ...yesOrNoRadio,
            name: "send_ripening_cycle_start_enabled",
            label: "Send ripening cycle start to external service",
            description: "When enabled, a per-pallet cycle-start message (end date only, no measurements) is sent to the tenant's external service (e.g. Bakker SAP) for every pallet allocated to a location when a ripening cycle starts, and again whenever the cycle is edited.",
            default_value: false,
        },
        {
            type: "element",
            name: "data_integration_collected",
            label: "Data collected from the integration",
            description: [
                "Which data kinds are collected from the connected external system (e.g. SAP).",
                "When 'Pallet movements' is enabled, moving a pallet in EYE is reported to the external",
                "system and the location update flows back from them (the local move is not stored to avoid",
                "duplicate in/out movements).",
                "When 'Users (SCIM provisioning)' is enabled, tenant admins can generate a SCIM token under",
                "Admin > Users > SCIM provisioning, so the customer's Entra ID can add and remove the users of this tenant.",
                "Turning it off stops provisioning immediately.",
            ].join(" "),
            el: <SingleSelect
                field="data_integration_collected"
                label="Data collected from the integration"
                multi
                return_array
                options={[
                    { value: "movements", label: "Pallet movements" },
                    { value: "scim_provisioning", label: "Users (SCIM provisioning)" },
                ]}
                post_value={["movements", "scim_provisioning"].filter((key) => config_data.data_integration?.[key])}
                setValue={(_, value) => setConfigData({
                    ...config_data,
                    data_integration: {
                        ...(config_data.data_integration || {}),
                        movements: (value || []).includes("movements"),
                        scim_provisioning: (value || []).includes("scim_provisioning"),
                    },
                })}
            />,
        },
        {
            type: "header",
            label: "Defects config",
        },
        {
            type: "element",
            el: <OptionsConfigForm
                fields={config_data.defect_positions || []}
                description="Overwrite default defect positions"
                name="defect_positions"
                useDisplayFilter={false}
                title="Options"
                setArray={(defect_positions) => setConfigData({ ...config_data, defect_positions })} />
        },
        {
            type: "element",
            el: <OptionsConfigForm
                fields={config_data.defect_severities || []}
                description="Overwrite default defect severities"
                name="defect_severities"
                useDisplayFilter={false}
                title="Options"
                setArray={(defect_severities) => setConfigData({ ...config_data, defect_severities })} />
        },
    ];

    return <MetaForm withSearch groupFields meta={form} setValue={(field, value) => setConfigData({ ...config_data, [field]: value })} object={config_data} config={config} />;
}
